import { useCallback, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Drawer, { DrawerBody, DrawerFooter, DrawerHeader } from '@/design_system/Drawer';
import InputSearch from '@/design_system/InputSearch';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import InputText from '@/design_system/InputText';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconEdit from '@/icons/Edit.svg';
import IconTrash from '@/icons/Trash.svg';
import { formatStreet, formatZip } from '@/models/address';
import { useEntities } from '@/models/entity';
import {
  EntityGroup,
  useCreateEntityGroup,
  useDeleteEntityGroup,
  useEntityGroups,
  useUpdateEntityGroup,
} from '@/models/entityGroup';
import useDebouncedState from '@/utils/useDebouncedState';
import useViewPort from '@/utils/useViewport';

const STORES_PER_PAGE = 10;

export const EntityGroups = () => {
  const { t } = useLingui();
  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), [setPage]);
  const [search, debouncedSearch, setSearch] = useDebouncedState('', 300, resetPage);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data: { entityGroups, meta } = {} } = useEntityGroups({
    limit: STORES_PER_PAGE,
    offset: (page - 1) * STORES_PER_PAGE,
    search: debouncedSearch,
  });

  const noData = entityGroups && entityGroups.length === 0;

  return (
    <>
      <Stack gap="40px">
        <Box padding="24px" gap="24px">
          <Stack row alignItems="center" justifyContent="space-between">
            <Title size="200" as="h2">
              <Trans id="settings.entityGroups.title">Groups</Trans>
            </Title>
            <Button
              variant="secondary"
              size="small"
              style={{ width: 'fit-content', padding: '8px' }}
              onPress={() => {
                setIsDrawerOpen(true);
              }}
            >
              <Stack row alignItems="center" gap="4px">
                <IconAdd />
                <InlineText size="200" weight="medium">
                  <Trans id="settings.entityGroups.addButton">Add a new group</Trans>
                </InlineText>
              </Stack>
            </Button>
          </Stack>
          <InputSearch
            ariaLabel={t({ id: 'settings.entityGroups.search.label', message: 'Search' })}
            placeholder={t({
              id: 'settings.entityGroups.search.placeholder',
              message: 'Search by name',
            })}
            style={{ flex: 1, minWidth: 175, maxWidth: 500 }}
            size="medium"
            value={search}
            onChange={setSearch}
          />

          {noData && (
            <Paragraph>
              <Trans id="settings.entityGroups.empty">No items for now</Trans>
            </Paragraph>
          )}
          {!noData && (
            <>
              <Table
                ariaLabel={t({ id: 'settings.entityGroups.table.label', message: 'Groups' })}
                columnWidths="auto minmax(200px, 1fr) minmax(200px, 1fr)"
              >
                <Header>
                  <Row>
                    <Column>
                      <Trans id="settings.entityGroups.column.name">Name</Trans>
                    </Column>
                    <Column>
                      <Trans id="settings.entityGroups.column.numberOfMembers">
                        Number of members
                      </Trans>
                    </Column>
                  </Row>
                </Header>
                <Body>
                  {entityGroups?.map((entityGroup) => (
                    <EntityGroupsRow key={entityGroup.id} entityGroup={entityGroup} />
                  ))}
                </Body>
                <PaginationFooter
                  page={page}
                  itemsPerPage={STORES_PER_PAGE}
                  count={meta?.count}
                  onPageChange={setPage}
                />
              </Table>
            </>
          )}
        </Box>
      </Stack>
      <EntityGroupDrawer
        entityGroup={null}
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
        mode="create"
      />
    </>
  );
};

const EntityGroupsRow = ({ entityGroup }: { entityGroup: EntityGroup }) => {
  const { t } = useLingui();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { mutate: deleteEntityGroup } = useDeleteEntityGroup();

  return (
    <>
      <Row>
        <Cell>
          <Stack style={{ flexDirection: 'row', gap: '10px' }}>
            <b>{entityGroup.name}</b>
          </Stack>
        </Cell>
        <Cell>
          <Stack>{entityGroup.entities?.length || 0}</Stack>
        </Cell>
        <Cell justifyContent="flex-end">
          <Stack row gap="8px">
            <Button
              variant="secondary"
              iconOnly
              size="small"
              ariaLabel={t({ id: 'settings.actions.actions.edit', message: 'Edit' })}
              onPress={() => {
                setIsDrawerOpen(true);
              }}
            >
              <IconEdit />
            </Button>
            <Button
              variant="secondary-danger"
              iconOnly
              size="small"
              ariaLabel={t({ id: 'settings.actions.actions.delete', message: 'Delete' })}
              onPress={() => {
                const userConfirmed = confirm(
                  t({
                    id: 'settings.entityGroups.delete.confirmation',
                    message: 'Are you sure you want to delete this group?',
                  })
                );

                if (userConfirmed) {
                  deleteEntityGroup(entityGroup.id);
                }
              }}
            >
              <IconTrash />
            </Button>
            <EntityGroupDrawer
              entityGroup={entityGroup}
              isOpen={isDrawerOpen}
              onClose={() => {
                setIsDrawerOpen(false);
              }}
              mode="edit"
            />
          </Stack>
        </Cell>
      </Row>
    </>
  );
};

const EntityGroupDrawer = ({
  entityGroup,
  isOpen,
  onClose,
  mode,
}: {
  entityGroup: EntityGroup | null;
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
}) => {
  const { isMobile } = useViewPort();
  const [entities, setEntities] = useState<NonNullable<EntityGroup['entities']>>(
    entityGroup?.entities || []
  );
  const [name, setName] = useState(entityGroup?.name);
  const { mutate: createEntityGroup } = useCreateEntityGroup();
  const { mutate: updateEntityGroup } = useUpdateEntityGroup();

  const handleSave = () => {
    if (mode === 'edit' && entityGroup) {
      updateEntityGroup({
        entityGroupId: entityGroup.id,
        body: {
          name,
          entitiesIds: entities.map((entity) => entity.id),
        },
      });
    }

    if (mode === 'create' && name) {
      createEntityGroup({
        name,
        entitiesIds: entities.map((entity) => entity.id),
      });
      setName(undefined);
      setEntities([]);
    }
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onOpenChange={onClose}>
      <>
        <DrawerHeader>
          <Title size="300">
            {mode === 'edit' ? (
              <Trans id="settings.entityGroups.edit.title">Update group</Trans>
            ) : (
              <Trans id="settings.entityGroups.create.title">Create group</Trans>
            )}
          </Title>
        </DrawerHeader>
        <DrawerBody>
          <Stack padding={isMobile ? '1rem' : '2rem'} gap="2rem">
            <EntityGroupNameInput name={name} onChange={setName} />
            <EntityGroupEntitiesTable
              entities={entities}
              onAdd={(entity) => {
                setEntities([...entities, entity]);
              }}
              onDelete={(entity) => {
                setEntities(entities.filter((e) => e.id !== entity.id));
              }}
            />
          </Stack>
        </DrawerBody>
        <DrawerFooter>
          <Stack row gap="12px" justifyContent="flex-end">
            <Button variant="secondary" onPress={onClose}>
              <Trans id="settings.actions.actions.cancel">Cancel</Trans>
            </Button>
            <Button variant="primary" onPress={handleSave}>
              {mode === 'edit' ? (
                <Trans id="settings.entityGroups.edit.saveButton">Save changes</Trans>
              ) : (
                <Trans id="settings.entityGroups.create.createButton">Create group</Trans>
              )}
            </Button>
          </Stack>
        </DrawerFooter>
      </>
    </Drawer>
  );
};

const EntityGroupNameInput = ({
  name,
  onChange,
}: {
  name?: string;
  onChange: (name: string) => void;
}) => {
  const { t } = useLingui();

  return (
    <InputText
      label={t({
        id: 'settings.actions.actions.drawer.name',
        message: `Name`,
      })}
      isRequired
      defaultValue={name}
      onChange={onChange}
    />
  );
};

const EntityGroupEntitiesTable = ({
  entities,
  onAdd,
  onDelete,
}: {
  entities: NonNullable<EntityGroup['entities']>;
  onAdd: (entity: NonNullable<EntityGroup['entities']>[number]) => void;
  onDelete: (entity: NonNullable<EntityGroup['entities']>[number]) => void;
}) => {
  const { t } = useLingui();
  const [showEntitySearchSelect, setShowEntitySearchSelect] = useState(false);

  return (
    <Stack gap="0.5rem">
      {!!entities.length && (
        <>
          <span className="label-100-medium">
            <Trans id="settings.entityGroup.drawer.title">Entities Groups</Trans>
          </span>
          <Table columnWidths={['minmax(200px, 1fr)', 'minmax(200px, 1fr)', 'minmax(auto, 80px)']}>
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.entityGroups.edit.entities.column.name">Name</Trans>
                </Column>
                <Column>
                  <Trans id="settings.entityGroups.edit.entities.column.address">Address</Trans>
                </Column>
                <Column></Column>
              </Row>
            </Header>
            <Body>
              {entities.map((entity) => (
                <Row key={entity.id}>
                  <Cell>{entity.name}</Cell>
                  <Cell>
                    {entity.address
                      ? formatStreet(entity.address) + ', ' + formatZip(entity.address)
                      : '-'}
                  </Cell>
                  <Cell>
                    <Button
                      variant="secondary-danger"
                      ariaLabel={t({
                        id: 'settings.entityGroups.edit.entities.remove',
                        message: 'Remove entity from group',
                      })}
                      iconOnly
                      onPress={() => {
                        onDelete(entity);
                      }}
                    >
                      <IconTrash />
                    </Button>
                  </Cell>
                </Row>
              ))}
            </Body>
          </Table>
        </>
      )}

      {showEntitySearchSelect ? (
        <EntitySearchSelect
          onSelect={(entity) => {
            if (entity) {
              onAdd({
                id: entity.id,
                name: entity.name,
                address: entity.address || null,
              });
            }
            setShowEntitySearchSelect(false);
          }}
          entitiesToExcludeFromSearch={entities}
          onBlur={() => setShowEntitySearchSelect(false)}
        />
      ) : (
        <Button
          variant="link-subtle"
          size="small"
          style={{ width: 'fit-content', padding: '8px' }}
          onPress={() => setShowEntitySearchSelect(true)}
        >
          <Stack row alignItems="center" gap="4px">
            <IconAdd />
            <InlineText size="200" weight="medium">
              <Trans id="settings.entityGroups.edit.entities.add">Add an entity to the group</Trans>
            </InlineText>
          </Stack>
        </Button>
      )}
    </Stack>
  );
};

const EntitySearchSelect = ({
  onSelect,
  entitiesToExcludeFromSearch = [],
  onBlur,
}: {
  onSelect: (entity: Endpoints['GET /entities']['response']['entities'][number] | null) => void;
  entitiesToExcludeFromSearch?: EntityGroup['entities'];
  onBlur: () => void;
}) => {
  const { t } = useLingui();

  const [query, debouncedQuery, setQuery] = useDebouncedState<string>('', 500);

  const { data: { entities } = { entities: [] }, isFetching } = useEntities({
    search: debouncedQuery || undefined,
  });

  const entityOptions = entities.filter(
    (entity) => !entitiesToExcludeFromSearch.find((e) => e.id === entity.id)
  );

  return (
    <InputSelect
      variant="add"
      aria-label={t({
        id: 'settings.entityGroups.edit.entities.search-select.label',
        message: 'Add an entity',
      })}
      placeholder={t({
        id: 'settings.entityGroups.edit.entities.search-select.placeholder',
        message: 'Search an entity to add...',
      })}
      isLoading={isFetching || query !== debouncedQuery}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      menuIsOpen
      value={null} // Allows to automatically clear the input when selecting a value
      inputValue={query}
      onInputChange={setQuery}
      filterOption={null} // Avoid react-select to filter options only based on their label
      options={entityOptions}
      getOptionValue={(entity) => (entity ? entity.id : '')}
      getOptionLabel={(entity) => (entity ? entity.name : '')}
      onChange={onSelect}
      onBlur={onBlur}
    />
  );
};
